import React, { useState, useEffect } from 'react';
import { 
  Star, 
  MessageSquare, 
  User, 
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  Edit,
  Send,
  X,
  Eye
} from 'lucide-react';
import apiService from '../services/api';
import Modal from './Modal';
import { API_BASE_URL } from '../config/api.config';
import { getApiOrigin } from '../utils/apiOrigin';

// Construire l'URL complète de l'avatar
const getAvatarUrl = (avatar) => {
  if (!avatar) return null;
  
  // Si c'est déjà une URL complète (http:// ou https://), la retourner telle quelle
  if (avatar.startsWith('http://') || avatar.startsWith('https://')) {
    return avatar;
  }
  
  // Si c'est un chemin relatif (/uploads/...), construire l'URL complète
  if (avatar.startsWith('/uploads/')) {
    const baseUrl = getApiOrigin(API_BASE_URL);
    return `${baseUrl}${avatar}`;
  }
  
  // Sinon, retourner null (avatar invalide)
  return null;
};

const AvisManager = ({ commercantId, commercant }) => {
  const [avis, setAvis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedAvis, setSelectedAvis] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showReponseModal, setShowReponseModal] = useState(false);
  const [reponseText, setReponseText] = useState('');
  const [savingReponse, setSavingReponse] = useState(false);
  const [stats, setStats] = useState({ moyenne: 0, total: 0 });
  const [avatarErrors, setAvatarErrors] = useState(new Set());

  useEffect(() => {
    if (commercantId) {
      loadAvis();
    }
  }, [commercantId]);

  const loadAvis = async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log(`⭐ Chargement avis pour commercantId: ${commercantId}`);
      const response = await apiService.request(`/avis/commercant/${commercantId}/gestion`);
      console.log('⭐ Réponse API avis:', response);
      
      if (response && response.data) {
        setAvis(Array.isArray(response.data) ? response.data : []);
        setStats(response.stats || { moyenne: 0, total: 0 });
      } else {
        setAvis([]);
        setStats({ moyenne: 0, total: 0 });
      }
    } catch (error) {
      console.error('❌ Erreur lors du chargement des avis:', error);
      setError(`Erreur: ${error.response?.data?.error || error.message}`);
      setAvis([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRepondre = (avisItem) => {
    setSelectedAvis(avisItem);
    setReponseText(avisItem.reponseCommercant || '');
    setShowReponseModal(true);
  };

  const handleSaveReponse = async () => {
    if (!selectedAvis || !reponseText.trim()) {
      alert('Veuillez saisir une réponse');
      return;
    }

    setSavingReponse(true);
    try {
      await apiService.request(`/avis/${selectedAvis.id}/reponse`, {
        method: 'PUT',
        body: JSON.stringify({ reponse: reponseText.trim() })
      });
      
      alert('Réponse enregistrée avec succès !');
      setShowReponseModal(false);
      setReponseText('');
      setSelectedAvis(null);
      loadAvis(); // Recharger les avis
    } catch (error) {
      console.error('❌ Erreur lors de la sauvegarde de la réponse:', error);
      alert(`Erreur: ${error.response?.data?.error || error.message}`);
    } finally {
      setSavingReponse(false);
    }
  };

  const renderStars = (note) => {
    return (
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-5 w-5 ${
              star <= note
                ? 'fill-yellow-400 text-yellow-400'
                : 'text-gray-300'
            }`}
          />
        ))}
        <span className="ml-2 text-sm font-medium text-gray-700">{note}/5</span>
      </div>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Date inconnue';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">Chargement des avis...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">{error}</p>
        <button
          onClick={loadAvis}
          className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
        >
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête avec statistiques */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Avis clients - {commercant?.nom || 'Commerçant'}
            </h3>
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2">
                <Star className="h-6 w-6 fill-yellow-400 text-yellow-400" />
                <div>
                  <div className="text-2xl font-bold text-gray-900">
                    {stats.moyenne.toFixed(1)}
                  </div>
                  <div className="text-sm text-gray-600">
                    {stats.total} avis{stats.total > 1 ? 's' : ''}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <button
            onClick={loadAvis}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-md hover:bg-gray-50 border border-gray-300"
          >
            Actualiser
          </button>
        </div>
      </div>

      {/* Liste des avis */}
      {avis.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Star className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600 text-lg">Aucun avis pour le moment</p>
          <p className="text-gray-500 text-sm mt-2">
            Les avis clients apparaîtront ici une fois qu'ils auront été laissés après une commande livrée.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {avis.map((avisItem) => (
            <div
              key={avisItem.id}
              className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  {/* En-tête avis */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      {getAvatarUrl(avisItem.user?.avatar) && !avatarErrors.has(avisItem.user?.id) ? (
                        <img
                          src={getAvatarUrl(avisItem.user.avatar)}
                          alt={avisItem.user.nom}
                          className="h-10 w-10 rounded-full object-cover"
                          onError={() => {
                            setAvatarErrors(prev => new Set([...prev, avisItem.user?.id]));
                          }}
                        />
                      ) : (
                        <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                          <User className="h-6 w-6 text-blue-600" />
                        </div>
                      )}
                      <div>
                        <div className="font-medium text-gray-900">
                          {avisItem.user?.prenom} {avisItem.user?.nom}
                        </div>
                        <div className="text-sm text-gray-500">
                          {formatDate(avisItem.createdAt)}
                        </div>
                      </div>
                    </div>
                    {renderStars(avisItem.note)}
                  </div>

                  {/* Commentaire */}
                  {avisItem.commentaire && (
                    <div className="mb-3">
                      <p className="text-gray-700">{avisItem.commentaire}</p>
                    </div>
                  )}

                  {/* Informations sur la commande/RDV */}
                  {(avisItem.commande || avisItem.rendezVous) && (
                    <div className="mb-3 text-sm text-gray-500">
                      {avisItem.commande && (
                        <span className="flex items-center">
                          <Calendar className="h-4 w-4 mr-1" />
                          Commande {avisItem.commande.numero}
                        </span>
                      )}
                      {avisItem.rendezVous && (
                        <span className="flex items-center">
                          <Clock className="h-4 w-4 mr-1" />
                          Rendez-vous du {formatDate(avisItem.rendezVous.dateDebut)}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Réponse du commerçant */}
                  {avisItem.reponseCommercant && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="flex items-start space-x-2">
                        <div className="flex-shrink-0">
                          <MessageSquare className="h-5 w-5 text-blue-600" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <span className="text-sm font-medium text-gray-900">
                              Réponse du commerçant
                            </span>
                            <span className="text-xs text-gray-500">
                              {formatDate(avisItem.dateReponse)}
                            </span>
                          </div>
                          <p className="text-gray-700 text-sm">{avisItem.reponseCommercant}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="ml-4 flex flex-col space-y-2">
                  <button
                    onClick={() => {
                      setSelectedAvis(avisItem);
                      setShowDetailModal(true);
                    }}
                    className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                    title="Voir les détails"
                  >
                    <Eye className="h-5 w-5" />
                  </button>
                  {!avisItem.reponseCommercant && (
                    <button
                      onClick={() => handleRepondre(avisItem)}
                      className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-md transition-colors"
                      title="Répondre à l'avis"
                    >
                      <MessageSquare className="h-5 w-5" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal de détail */}
      {showDetailModal && selectedAvis && (
        <Modal
          isOpen={showDetailModal}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedAvis(null);
          }}
          title="Détail de l'avis"
        >
          <div className="space-y-4">
            <div className="flex items-center space-x-3 pb-4 border-b">
              {getAvatarUrl(selectedAvis.user?.avatar) && !avatarErrors.has(selectedAvis.user?.id) ? (
                <img
                  src={getAvatarUrl(selectedAvis.user.avatar)}
                  alt={selectedAvis.user.nom}
                  className="h-12 w-12 rounded-full object-cover"
                  onError={() => {
                    setAvatarErrors(prev => new Set([...prev, selectedAvis.user?.id]));
                  }}
                />
              ) : (
                <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <User className="h-6 w-6 text-blue-600" />
                </div>
              )}
              <div>
                <div className="font-medium text-gray-900">
                  {selectedAvis.user?.prenom} {selectedAvis.user?.nom}
                </div>
                <div className="text-sm text-gray-500">
                  {formatDate(selectedAvis.createdAt)}
                </div>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Note</label>
              <div className="mt-1">{renderStars(selectedAvis.note)}</div>
            </div>

            {selectedAvis.commentaire && (
              <div>
                <label className="text-sm font-medium text-gray-700">Commentaire</label>
                <p className="mt-1 text-gray-700 bg-gray-50 p-3 rounded-md">
                  {selectedAvis.commentaire}
                </p>
              </div>
            )}

            {selectedAvis.commande && (
              <div>
                <label className="text-sm font-medium text-gray-700">Commande</label>
                <p className="mt-1 text-gray-700">
                  {selectedAvis.commande.numero} - {formatDate(selectedAvis.commande.dateCommande)}
                </p>
              </div>
            )}

            {selectedAvis.rendezVous && (
              <div>
                <label className="text-sm font-medium text-gray-700">Rendez-vous</label>
                <p className="mt-1 text-gray-700">
                  {formatDate(selectedAvis.rendezVous.dateDebut)}
                </p>
              </div>
            )}

            {selectedAvis.reponseCommercant && (
              <div>
                <label className="text-sm font-medium text-gray-700">Votre réponse</label>
                <p className="mt-1 text-gray-700 bg-blue-50 p-3 rounded-md">
                  {selectedAvis.reponseCommercant}
                </p>
                <p className="mt-1 text-xs text-gray-500">
                  Répondu le {formatDate(selectedAvis.dateReponse)}
                </p>
              </div>
            )}

            <div className="flex space-x-3 pt-4 border-t">
              {!selectedAvis.reponseCommercant && (
                <button
                  onClick={() => {
                    setShowDetailModal(false);
                    handleRepondre(selectedAvis);
                  }}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center justify-center space-x-2"
                >
                  <MessageSquare className="h-4 w-4" />
                  <span>Répondre</span>
                </button>
              )}
              <button
                onClick={() => {
                  setShowDetailModal(false);
                  setSelectedAvis(null);
                }}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
              >
                Fermer
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Modal de réponse */}
      {showReponseModal && selectedAvis && (
        <Modal
          isOpen={showReponseModal}
          onClose={() => {
            setShowReponseModal(false);
            setReponseText('');
            setSelectedAvis(null);
          }}
          title="Répondre à l'avis"
        >
          <div className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-md">
              <div className="flex items-center space-x-2 mb-2">
                {renderStars(selectedAvis.note)}
              </div>
              {selectedAvis.commentaire && (
                <p className="text-gray-700 text-sm">{selectedAvis.commentaire}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Votre réponse
              </label>
              <textarea
                value={reponseText}
                onChange={(e) => setReponseText(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Merci pour votre avis ! Nous sommes ravis que..."
                maxLength={1000}
              />
              <p className="mt-1 text-xs text-gray-500">
                {reponseText.length} / 1000 caractères
              </p>
            </div>

            <div className="flex space-x-3 pt-4 border-t">
              <button
                onClick={handleSaveReponse}
                disabled={savingReponse || !reponseText.trim()}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                {savingReponse ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Enregistrement...</span>
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    <span>Envoyer la réponse</span>
                  </>
                )}
              </button>
              <button
                onClick={() => {
                  setShowReponseModal(false);
                  setReponseText('');
                  setSelectedAvis(null);
                }}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
              >
                Annuler
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default AvisManager;

